/**
 * Utility functions to replace common jQuery methods
 */

/**
 * Fade in an element
 * @param {HTMLElement} element - The element to fade in
 * @param {number} duration - Duration in milliseconds (default: 400)
 * @param {string} display - Display property to set (default: 'block')
 */
export function fadeIn(element, duration = 400, display = "block") {
  if (!element) return;

  // Set initial state
  element.style.opacity = "0";
  element.style.display = display;

  const startTime = performance.now();

  function animate(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    element.style.opacity = progress.toString();

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  }

  requestAnimationFrame(animate);
}

/**
 * Fade out an element
 * @param {HTMLElement} element - The element to fade out
 * @param {number} duration - Duration in milliseconds (default: 400)
 */
export function fadeOut(element, duration = 400) {
  if (!element) return;

  const startTime = performance.now();
  const startOpacity = parseFloat(window.getComputedStyle(element).opacity) || 1;

  function animate(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    element.style.opacity = (startOpacity * (1 - progress)).toString();

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      element.style.display = "none";
    }
  }

  requestAnimationFrame(animate);
}

/**
 * Toggle class on an element
 * @param {HTMLElement} element - The element
 * @param {string} className - Class name to toggle
 */
export function toggleClass(element, className) {
  if (!element) return;
  element.classList.toggle(className);
}

/**
 * Add class to an element
 * @param {HTMLElement} element - The element
 * @param {string} className - Class name to add
 */
export function addClass(element, className) {
  if (!element) return;
  element.classList.add(className);
}

/**
 * Remove class from an element
 * @param {HTMLElement} element - The element
 * @param {string} className - Class name to remove
 */
export function removeClass(element, className) {
  if (!element) return;
  element.classList.remove(className);
}

/**
 * Check if element has a class
 * @param {HTMLElement} element - The element
 * @param {string} className - Class name to check
 * @returns {boolean}
 */
export function hasClass(element, className) {
  if (!element) return false;
  return element.classList.contains(className);
}

/**
 * Show an element
 * @param {HTMLElement} element - The element to show
 * @param {string} display - Display property (default: 'block')
 */
export function show(element, display = "block") {
  if (!element) return;
  element.style.display = display;
}

/**
 * Hide an element
 * @param {HTMLElement} element - The element to hide
 */
export function hide(element) {
  if (!element) return;
  element.style.display = "none";
}

/**
 * Get or set CSS property
 * @param {HTMLElement} element - The element
 * @param {string|object} property - CSS property name or object of properties
 * @param {string} value - Value to set (if property is string)
 * @returns {string|void} - Returns value if getting, void if setting
 */
export function css(element, property, value) {
  if (!element) return;

  // Get computed style
  if (typeof property === "string" && value === undefined) {
    return window.getComputedStyle(element)[property];
  }

  // Set single property
  if (typeof property === "string" && value !== undefined) {
    element.style[property] = value;
    return;
  }

  // Set multiple properties
  if (typeof property === "object") {
    Object.keys(property).forEach((key) => {
      element.style[key] = property[key];
    });
  }
}

/**
 * Get or set attribute
 * @param {HTMLElement} element - The element
 * @param {string} name - Attribute name
 * @param {string} value - Value to set (optional)
 * @returns {string|void}
 */
export function attr(element, name, value) {
  if (!element) return;

  if (value === undefined) {
    return element.getAttribute(name);
  }

  element.setAttribute(name, value);
}

/**
 * Remove attribute
 * @param {HTMLElement} element - The element
 * @param {string} name - Attribute name
 */
export function removeAttr(element, name) {
  if (!element) return;
  element.removeAttribute(name);
}

/**
 * Get or set data attribute
 * @param {HTMLElement} element - The element
 * @param {string} key - Data attribute key
 * @param {string} value - Value to set (optional)
 * @returns {string|void}
 */
export function data(element, key, value) {
  if (!element) return;

  if (value === undefined) {
    return element.dataset[key];
  }

  element.dataset[key] = value;
}

/**
 * Get text content
 * @param {HTMLElement} element - The element
 * @returns {string}
 */
export function text(element) {
  if (!element) return "";
  return element.textContent;
}

/**
 * Set text content
 * @param {HTMLElement} element - The element
 * @param {string} value - Text to set
 */
export function setText(element, value) {
  if (!element) return;
  element.textContent = value;
}

/**
 * Get or set HTML content
 * @param {HTMLElement} element - The element
 * @param {string} value - HTML to set (optional)
 * @returns {string|void}
 */
export function html(element, value) {
  if (!element) return;

  if (value === undefined) {
    return element.innerHTML;
  }

  element.innerHTML = value;
}

/**
 * Check if element is visible
 * @param {HTMLElement} element - The element
 * @returns {boolean}
 */
export function isVisible(element) {
  if (!element) return false;
  return element.offsetWidth > 0 || element.offsetHeight > 0;
}

/**
 * Slide down (show with animation)
 * @param {HTMLElement} element - The element
 * @param {number} duration - Duration in milliseconds
 */
export function slideDown(element, duration = 400) {
  if (!element) return;

  element.style.removeProperty("display");
  let display = window.getComputedStyle(element).display;
  if (display === "none") display = "block";

  element.style.display = display;
  const height = element.offsetHeight;

  element.style.overflow = "hidden";
  element.style.height = "0";
  element.style.paddingTop = "0";
  element.style.paddingBottom = "0";
  element.style.marginTop = "0";
  element.style.marginBottom = "0";

  element.offsetHeight; // Force reflow

  element.style.transition = `height ${duration}ms ease, padding ${duration}ms ease, margin ${duration}ms ease`;
  element.style.height = height + "px";
  element.style.removeProperty("padding-top");
  element.style.removeProperty("padding-bottom");
  element.style.removeProperty("margin-top");
  element.style.removeProperty("margin-bottom");

  setTimeout(() => {
    element.style.removeProperty("height");
    element.style.removeProperty("overflow");
    element.style.removeProperty("transition");
  }, duration);
}

/**
 * Slide up (hide with animation)
 * @param {HTMLElement} element - The element
 * @param {number} duration - Duration in milliseconds
 */
export function slideUp(element, duration = 400) {
  if (!element) return;

  element.style.height = element.offsetHeight + "px";
  element.style.overflow = "hidden";

  element.offsetHeight; // Force reflow

  element.style.transition = `height ${duration}ms ease, padding ${duration}ms ease, margin ${duration}ms ease`;
  element.style.height = "0";
  element.style.paddingTop = "0";
  element.style.paddingBottom = "0";
  element.style.marginTop = "0";
  element.style.marginBottom = "0";

  setTimeout(() => {
    element.style.display = "none";
    element.style.removeProperty("height");
    element.style.removeProperty("padding-top");
    element.style.removeProperty("padding-bottom");
    element.style.removeProperty("margin-top");
    element.style.removeProperty("margin-bottom");
    element.style.removeProperty("overflow");
    element.style.removeProperty("transition");
  }, duration);
}
